import moment from 'moment-timezone';
import { Dialog, LayoutBox, NavBackButton, NavBar, NavGroup, NavRow, Page, TextData } from '../../components';
import PageTitleAndAction from '../../components/PageTitleAndActions';
import { useApi, useAsync, useNavigate } from '../../hooks';
import { Box, Button, FileInput, GridModal, Heading, Paragraph, Spacing, Text, TextField, Toast } from '../../ui';
import { QuoteStat } from '../QuotePage/content/QuoteView/QuoteStat';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import DropdownMenu from '../../components/DropdownMenu';
import { PolicyPageContextProvider, usePolicyPageContext } from '../PolicyPage/context/PolicyPageContext';
import { ExecuteMCEModal, LoaderModal, SaveDraftMCEModal } from '../../organisms/MCEModals';
import { PolicyTabs } from '../PolicyPage/content/PolicyView/PolicyTabs';
import { ActivityIndicator } from 'react-native-web';
import { MCEPageContextProvider, useMCEPageContext } from './context/MCEPageContext';
import { capitalize } from '../../ui/utils';
import { currencyFormatter } from '../QuotePage/content/QuoteGrids/utils';
import { useParams } from 'react-router-dom';
import ReverseMCEModal from '../../organisms/MCEModals/ReverseMCEModal';

const AddMCENote = memo(function AddMCENote(props) {
  const { id, notes = '', ...rest } = props.mce;
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const { setMCEDetails } = useMCEPageContext();

  const handleAddNote = useCallback(() => {
    setMCEDetails({ ...props.mce, notes: note });
    setOpen(false);
  }, [note]);

  useEffect(() => {
    if (open) {
      setNote(notes || '');
    }
  }, [open, notes]);

  return (
    <>
      <Button color="$primary.light" variant="text" label="Update" onPress={() => setOpen(true)} {...rest} />
      <Dialog
        open={open}
        heading="Update MCE Notes"
        onClose={() => setOpen(false)}
        actions={
          <>
            <Button label="Cancel" variant="text" color="$primary" onPress={() => setOpen(false)} />
            <Button label="Submit" onPress={() => handleAddNote(note)} disabled={!note.trim()} color={!note ? '$gray.500' : '$primary'} />
          </>
        }
      >
        <TextField
          InputProps={{ multiline: true }}
          multiline
          height={200}
          width="100%"
          maxWidth="100%"
          value={note}
          onChangeValue={(v) => setNote(v)}
        />
      </Dialog>
    </>
  );
});

const MCENotes = ({ mce, isEditing, policyId }) => (
  <Box>
    <Box display={'flex'} alignItems={'start'} justifyContent={'space-between'} backgroundColor="#fff" padding={24} borderRadius={8} gap={16}>
      <Heading level={5}>Notes</Heading>
      <Paragraph style={{ color: '#637381', fontWeight: '500' }}>{mce.notes || 'No MCE notes'}</Paragraph>
      {isEditing ? <AddMCENote id={mce.id} currentDescription={mce.notes} alignSelf="flex-start" mce={mce} policyId={policyId} /> : null}
    </Box>
  </Box>
);

const NewMiscPage = ({ updateMCE, executeMCE }) => {
  const navigate = useNavigate();
  const scrollRef = useRef();
  const {
    downloadMCE,
    addAdjustments,
    updateAdjustments,
    supersedeMCE,
    deletesupersededMCE,
    reverseMCE,
    runMacavityForMCE,
    getMacavityForMCE,
    downloadMonthlyStatementById,
  } = useApi();
  const { policyId, MCEId } = useParams();

  const {
    coveragesData,
    currentTab,
    setCurrentTab,
    setShouldShowWarning,
    shouldShowWarning,
    setShowSplitModal = () => {},
    showSplitModal,
    policy,
    status,
    monthlyReportingData,
  } = usePolicyPageContext();

  const {
    MCEDetails,
    isEditing,
    setIsEditing,
    premiumData,
    refreshMCE,
    setMCEDetails,
    showExecuteButton,
    MCEStatus,
    resetMCE,
    deletedLocations,
    setDeletedLocations,
    setPremiumData,
    isAdjusting,
    setIsAdjusting,
  } = useMCEPageContext();
  const [shouldShowDraftModal, setShouldShowDraftModal] = useState(false);
  const [shouldExecute, setShouldExecute] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldReverse, setShouldReverse] = useState(false);
  // Top-of-screen message, e.g. when a quarterly (statement-derived) MCE save is blocked (409).
  const [errorBanner, setErrorBanner] = useState(null);

  const ALL_TABS = {
    PREMIUM_CALCULATION: 'PREMIUM_CALCULATION',
    MCE_FEES: 'MCE_FEES',
    LOCATION_DETAILS: 'LOCATION_DETAILS',
  };

  const tabs = Object.entries(ALL_TABS).reduce((acc, [key, value]) => {
    if (MCEDetails?.type === 'legacy' && key !== 'MCE_FEES') {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setIsEditing(false);
    }, 3000);
  };

  const generateFormattedPayload = (premiumData) => {
    const updatedMCEDetails = { ...MCEDetails };
    if (MCEDetails.type === 'legacy') {
      return updatedMCEDetails;
    }
    updatedMCEDetails.calculations = updatedMCEDetails.calculations.map((location) => {
      const matchingPremium = premiumData.find((item) => item.locationId === location.locationId);
      if (matchingPremium) {
        return {
          ...location,
          declaredInventory: matchingPremium.avg_declared_inventory,
        };
      }
      return location;
    });

    updatedMCEDetails.locations = updatedMCEDetails.locations.map((location) => {
      const matchingLocation = premiumData.find((loc) => loc.locationId === location.id);
      if (matchingLocation) {
        const newDeclaredInventory = matchingLocation?.avg_declared_inventory || 0;
        const originalCoveredValue = location.coveredValue;

        location.coveredValue = newDeclaredInventory;

        // Update the payoutLimit in coverages to match the new coveredValue (only if it was equal to the original coveredValue)
        location.coverages = location.coverages.map((coverage) => {
          if (coverage.payoutLimit === originalCoveredValue) {
            coverage.payoutLimit = newDeclaredInventory;
          }
          return coverage;
        });

        if (matchingLocation?.isRowDeleted) {
          location.deleted = `${MCEDetails.effectiveAt} 00:00:00`;
        }
      }
      return location;
    });
    const newLocations = premiumData.filter((loc) => loc.newLocation).map(({ newLocation }) => ({ ...newLocation }));
    updatedMCEDetails.locations = [...updatedMCEDetails.locations, ...newLocations];
    return updatedMCEDetails;
  };

  const { execute: executeUpdate, status: updateStatus } = useAsync(updateMCE, { immediate: false });

  const handleSave = async () => {
    try {
      const formattedPayload = generateFormattedPayload(premiumData);

      if (!formattedPayload || typeof formattedPayload !== 'object') {
        throw new Error('Invalid payload format');
      }

      await executeUpdate(policyId, MCEId, formattedPayload);

      setLoading(true);
      await refreshMCE();
    } catch (error) {
      console.error('Error saving data:', error);

      const status = error?.status ?? error?.response?.status;
      if (status === 409) {
        // Quarterly (statement-derived) MCEs are locked from edits — surface a top banner.
        setErrorBanner('Quarterly Statements cannot be currently updated');
      } else if (status === 400 && typeof error?.data === 'string' && error.data) {
        // The server explains a rejected edit (e.g. a rate override on a location whose value doesn't change).
        setErrorBanner(error.data);
      } else {
        alert('Failed to save data. Please try again later.');
      }
    } finally {
      setIsEditing(false);
      setDeletedLocations([]);
      setLoading(false);
    }
  };

  const handleExecuteMCE = async () => {
    try {
      await executeMCE(policyId, MCEId);
      await runMacavityForMCE(MCEId);

      let attempts = 0;
      let macavityData = null;
      while (attempts < 10) {
        macavityData = await getMacavityForMCE(MCEId);
        if (macavityData && macavityData.length > 0) break;
        await new Promise((res) => setTimeout(res, 2000));
        attempts++;
      }

      if (!macavityData || macavityData.length === 0) {
        console.warn('Macavity data still empty after polling');
        alert('Macavity results are not ready yet. Please refresh in a few moments.');
      }

      setShouldExecute(false);
      setLoading(true);
      await refreshMCE();
    } catch (error) {
      console.error('Error executing MCE:', error);
      alert('Failed to execute MCE. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const ActionButtons = () => {
    const [open, setOpen] = useState(false);
    const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
    const [isSupersedeInProgress, setIsSupersedeInProgress] = useState(false);
    const [isReverseInProgress, setIsReverseInProgress] = useState(false);
    const { combinedMode, setCombinedMode } = usePolicyPageContext();
    const { setMonthlyData, quarterlyBreakdown } = useMCEPageContext();
    const { getMonthlyReportingForMCE } = useApi();
    const isQuarterly = MCEDetails?.type === 'quarterly';

    const handleCombinedModeToggle = async () => {
      try {
        const newCombinedMode = !combinedMode;
        const data = await getMonthlyReportingForMCE(policyId, MCEDetails.id, !newCombinedMode);
        if (data) {
          setCombinedMode(newCombinedMode);
          setMonthlyData(data);
        }
      } catch (error) {
        console.error('Failed to fetch MCE monthly data:', error);
      }
    };

    const navigateToPolicy = React.useCallback(
      (policy) => {
        if (policy) {
          navigate.to(`/policies/${policy.id}`);
        }
      },
      [navigate]
    );

    const handleCancelDelete = () => {
      try {
        if (deletedLocations.length > 0) {
          const updatedLocations = MCEDetails.locations.map((location) => {
            const wasDeleted = deletedLocations.some((deletedLocation) => deletedLocation.riskId === location.riskId);

            if (wasDeleted) {
              return { ...location, deleted: null };
            }
            return location;
          });

          setMCEDetails({
            ...MCEDetails,
            locations: updatedLocations,
          });

          setDeletedLocations([]);
        }
      } catch (error) {
        console.error('Failed to revert MCE details:', error);
        alert('An error occurred while reverting the MCE details. Please try again.');
      }
    };
    const handleDownloadMCE = async () => {
      setIsDownloadInProgress(true);
      try {
        if (!MCEDetails.id) {
          alert('Please select an MCE to download.');
          return;
        }

        if (isQuarterly) {
          const statementId = quarterlyBreakdown?.statementId;
          if (!statementId) {
            alert('No monthly statement found for this quarterly MCE.');
            return;
          }
          const response = await downloadMonthlyStatementById(policyId, statementId);
          if (response && response.request && response.request.responseURL) {
            window.open(response.request.responseURL, '_blank');
          } else {
            alert('Failed to download the monthly statement. Please try again.');
          }
        } else {
          await runMacavityForMCE(MCEDetails.id);
          let attempts = 0;
          let macavityData = null;
          while (attempts < 10) {
            macavityData = await getMacavityForMCE(MCEDetails.id);
            if (macavityData && macavityData.length > 0) break;
            await new Promise((res) => setTimeout(res, 2000));
            attempts++;
          }

          if (!macavityData || macavityData.length === 0) {
            console.warn('Macavity data still empty, downloading anyway');
          }
          const response = await downloadMCE(MCEDetails.id, policyId);
          if (response && response.request && response.request.responseURL) {
            window.open(response.request.responseURL, '_blank');
          } else {
            alert('Failed to download the MCE. Please try again.');
          }
        }
      } catch (error) {
        alert('An error occurred while trying to download. Please try again.');
        console.error(error);
      } finally {
        setIsDownloadInProgress(false);
      }
    };

    const handleSupersedeMCE = async () => {
      setIsSupersedeInProgress(true);
      try {
        if (MCEDetails.id) {
          const response = await supersedeMCE(MCEDetails.id, policyId);
          if (response) {
            navigateToPolicy(policy);
          }
        } else {
          alert('Please select an MCE to supersede.');
        }
      } catch (error) {
        alert('An error occurred while trying to supersede the MCE. Please try again.');
        console.error(error);
      } finally {
        setIsSupersedeInProgress(false);
      }
    };

    const handleReverseMCE = async () => {
      setIsReverseInProgress(true);
      try {
        if (MCEDetails.id) {
          const response = await reverseMCE(MCEDetails.id, policyId);
          if (response) {
            navigateToPolicy(policy);
          }
        } else {
          alert('Please select an MCE to reverse.');
        }
      } catch (error) {
        alert('An error occurred while trying to reverse the MCE. Please try again.');
        console.error(error);
      } finally {
        setIsReverseInProgress(false);
      }
    };

    const handleDeleteSupersedeMCE = useCallback(async () => {
      const response = await deletesupersededMCE(MCEDetails.supersedes, policyId, MCEDetails.id);
      if (response?.status === 204) {
        navigateToPolicy(policy);
      }
      return response;
    }, [policy.id]);

    const { execute: deleteSupersedeMCE } = useAsync(handleDeleteSupersedeMCE, { immediate: false });

    return (
      <LayoutBox row layout="center-right" gap={12} style={{ flexWrap: 'wrap', gap: '6px', display: 'flex' }}>
        {MCEDetails.type === 'retrospective' && (
          <Button target="_blank" color="$primary" rel="noreferrer" onPress={handleCombinedModeToggle} borderRadius={8}>
            {combinedMode ? 'Show Uncombined' : 'Show Combined'}
          </Button>
        )}
        {MCEStatus === 'executed' && (
          <>
            <Button
              target="_blank"
              color="$primary"
              rel="noreferrer"
              onPress={handleSupersedeMCE}
              borderRadius={8}
              loading={isSupersedeInProgress}
              disabled={isSupersedeInProgress}
            >
              Supersede
            </Button>
          </>
        )}
        {(MCEStatus === 'executed' || MCEStatus === 'released') &&
          (MCEDetails.type === 'locationChange' || MCEDetails.type === 'retrospective') &&
          !MCEDetails.reverses && (
            <>
              <Button
                target="_blank"
                color="#eb0101ff"
                rel="noreferrer"
                onPress={() => setShouldReverse(true)}
                borderRadius={8}
                disabled={isReverseInProgress}
              >
                Reverse
              </Button>
            </>
          )}

        <Button
          target="_blank"
          color="$primary"
          rel="noreferrer"
          onPress={handleDownloadMCE}
          borderRadius={8}
          loading={isDownloadInProgress}
          disabled={isDownloadInProgress}
        >
          {isQuarterly ? 'Download Monthly Statement' : 'Download'}
        </Button>
        {isEditing && MCEStatus !== 'executed' && (
          <LayoutBox row layout="center-right" gap={12}>
            <Button
              target="_blank"
              color="$primary"
              rel="noreferrer"
              onPress={() => {
                setIsEditing(false);
                setIsAdjusting(false);
                resetMCE();
              }}
              borderRadius={8}
            >
              Cancel
            </Button>
            <Button
              loading={updateStatus === 'pending'}
              disabled={updateStatus === 'pending'}
              target="_blank"
              color="$primary"
              rel="noreferrer"
              onPress={() => {
                if (!!deletedLocations.length) {
                  setOpen(true);
                } else handleSave();
              }}
              borderRadius={8}
            >
              Save
            </Button>
          </LayoutBox>
        )}
        {!isEditing && MCEStatus !== 'executed' && MCEStatus !== 'superseded' && (
          <Button
            target="_blank"
            color="$primary"
            rel="noreferrer"
            onPress={() => {
              setIsEditing(true);
            }}
            borderRadius={8}
          >
            Edit
          </Button>
        )}
        {showExecuteButton && (
          <Button
            target="_blank"
            color="$primary"
            borderRadius={8}
            onPress={() => {
              setShouldExecute(true);
            }}
            label="Execute"
          ></Button>
        )}
        <DropdownMenu
          options={[
            { label: 'Duplicate' },
            { label: 'Delete' },
            ...(MCEDetails?.supersedes ? [{ label: 'Delete and apply adjustment', onClick: () => deleteSupersedeMCE() }] : []),
          ]}
        />
        <ReverseMCEModal
          showModal={shouldReverse}
          setShowModal={setShouldReverse}
          handlePress={handleReverseMCE}
          loading={isReverseInProgress}
          disabled={isReverseInProgress}
        />

        <GridModal
          open={open}
          title="Are You Sure You Want to Delete?"
          onClose={() => {
            setOpen(false);
          }}
          buttonText="Delete Permanently"
          deleteModal={true}
          handlePress={handleSave}
          capitalizeTitle={true}
          onCancel={handleCancelDelete}
        >
          <Text align="left" size="large" style={{ fontWeight: 500, padding: 16 }}>
            This action cannot be undone.
          </Text>
        </GridModal>
      </LayoutBox>
    );
  };

  const MiscOverViewSection = ({ policy }) => {
    const { effectiveAt, effectiveUntil, status, mceNumber, netPremium } = MCEDetails;
    const MCEStatsData = [
      {
        title: 'Additional premium',
        text: ` ${currencyFormatter({ field: { value: netPremium }, withDecimals: true })}`,
      },
      {
        title: 'Effective At',
        text: `${effectiveAt}`,
      },
      {
        title: 'Effective Until',
        text: `${effectiveUntil || moment.utc(policy.effectiveUntil).format('YYYY-MM-DD')}`,
      },
    ];

    return (
      <LayoutBox paddingTop="40px" paddingX="40px" gap={16}>
        <Box width="100%">
          <PageTitleAndAction
            data={policy}
            HeaderTitle={policy?.insured}
            showHeroIcon={false}
            status={capitalize(status)}
            itemNumber={mceNumber}
            actionButtons={ActionButtons}
            effectiveDate={moment(effectiveAt)}
            type={MCEDetails.type}
            adjustment={!!MCEDetails?.adjustment}
          />
        </Box>
        <LayoutBox width="100%" zIndex={-1}>
          <QuoteStat data={MCEStatsData} />
        </LayoutBox>
        <LayoutBox gap={18}>
          <MCENotes mce={MCEDetails} isEditing={isEditing} policyId={policy.id} />
        </LayoutBox>
      </LayoutBox>
    );
  };

  const AttachmentSection = ({ policy }) => {
    return (
      <LayoutBox
        style={{
          display: ' flex',
          padding: ' 24px',
          flexDirection: 'column',
          alignteIms: 'flex-start',
          gap: ' 16px',
          alignSelf: 'stretch',
          backgroundColor: 'white',
        }}
      >
        <LayoutBox>
          <Attachments policy={{}} attachments={policy?.attachments} />
        </LayoutBox>
      </LayoutBox>
    );
  };

  const AdjustmentSection = () => {
    const [adjustmentInProgress, setIsAdjustmentInProgress] = useState(false);
    const handleAdjustmentUpdate = async () => {
      try {
        setIsAdjustmentInProgress(true);
        const adjustment = MCEDetails?.adjustment ?? {};
        const amount = adjustment?.amount ?? 0;
        const id = adjustment?.id ?? '';
        if (id) {
          await updateAdjustments(MCEId, policyId, id, { amount });
        } else {
          await addAdjustments(MCEId, policyId, { amount });
        }
        await runMacavityForMCE(MCEId);
        let attempts = 0;
        let macavityData = null;
        while (attempts < 10) {
          macavityData = await getMacavityForMCE(MCEId);
          if (macavityData && macavityData.length > 0) break;
          await new Promise((res) => setTimeout(res, 2000));
          attempts++;
        }
        if (!macavityData || macavityData.length === 0) {
          console.warn('Macavity data not ready after adjustment');
          alert('Adjustment saved. Macavity results will be available shortly.');
        }

        setLoading(true);
        await refreshMCE();
      } catch (error) {
        console.error('Error saving data:', error);
        alert('Failed to save data. Please try again later.');
      } finally {
        setLoading(false);
        setIsAdjusting(false);
        setIsAdjustmentInProgress(false);
      }
    };

    return (
      <LayoutBox
        style={{
          display: ' flex',
          padding: ' 24px',
          flexDirection: 'column',
          alignteIms: 'flex-start',
          gap: ' 16px',
          alignSelf: 'stretch',
          backgroundColor: 'white',
        }}
      >
        <LayoutBox>
          <LayoutBox row justifyContent="space-between" alignItems="flex-end">
            <Heading level={3} small weight="700px" bold>
              Adjustment
            </Heading>
            <LayoutBox row layout="bottom-right" gap={12}>
              <Button label={MCEDetails?.adjustment ? 'Edit Adjustment' : 'Add'} onPress={() => setIsAdjusting(true)} color={'$primary'} />
              <Button
                label={'Save'}
                onPress={() => handleAdjustmentUpdate()}
                color={'$primary'}
                loading={adjustmentInProgress}
                disabled={adjustmentInProgress}
              />
            </LayoutBox>
          </LayoutBox>
        </LayoutBox>{' '}
      </LayoutBox>
    );
  };

  const Attachments = ({ policy, attachments = [] }) => {
    const { id } = policy;
    const [inputValue, setInputValue] = useState(null);
    const lastInputValue = useRef(inputValue);

    const { uploadPolicyAttachment } = useApi();
    const uploadDocument = useCallback(
      async (document) => {
        await uploadPolicyAttachment(id, {
          document,
        });
        // await refreshApplicationData(true);
      },
      [uploadPolicyAttachment, id]
    );

    const { execute } = useAsync(uploadDocument, { immediate: false });

    useEffect(() => {
      // attachment upload
      if (inputValue && inputValue !== lastInputValue.current) {
        lastInputValue.current = inputValue;

        execute(inputValue);
      }
    }, [inputValue, execute]);

    attachments.sort((a, b) => {
      if (a.name > b.name) return 1;
      else if (a.name < b.name) return -1;
      else return 0;
    });

    const tdProps = {
      labelProps: { size: 'small' },
      invert: true,
      flex: 1,
    };

    return (
      <LayoutBox>
        <LayoutBox row justifyContent="space-between" alignItems="flex-end">
          <Heading level={3} small weight="700px" bold>
            Attachments
          </Heading>
          <LayoutBox row layout="bottom-right" gap={12}>
            <FileInput
              value={inputValue}
              onChangeValue={(files) => {
                if (files.length) {
                  setInputValue(files[0]);
                }
              }}
            />
          </LayoutBox>
        </LayoutBox>
        {!!attachments.length && <Spacing vertical={1.5} border={{ bottom: { width: 1, color: '$gray.200' } }} />}
        {attachments.map((attachment) => (
          <LayoutBox row flex={1} key={`${attachment.attachment_id}`}>
            <TextData label="Name" data={attachment.name} href={attachment.url} {...tdProps} textProps={{ color: '$primary' }} />
            <TextData label="Modified" data={moment(attachment.lastModified).format('MM-DD-YYYY')} {...tdProps} />
          </LayoutBox>
        ))}
        {!!attachments.length && <Spacing vertical={5} />}
      </LayoutBox>
    );
  };

  const handleAddLocation = () => {
    setIsEditing(true);
  };

  const handleNavigate = () => {
    if (isEditing) setShouldShowDraftModal(true);
    else {
      setShouldShowDraftModal(false);
      setIsEditing(false);
      navigate.back(-1);
    }
  };

  const Spinner = () => {
    return (
      <LayoutBox style={{ position: 'relative', minHeight: '100vh' }} width="100%">
        <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 999, margin: 'auto' }}>
          <ActivityIndicator />
        </Box>
      </LayoutBox>
    );
  };

  useEffect(() => {
    if (currentTab !== 'MCE_FEES' && isAdjusting) setIsAdjusting(false);
  }, [currentTab, isAdjusting]);

  return (
    <LayoutBox width="100%" ref={scrollRef}>
      <Toast
        open={!!errorBanner}
        onClose={() => setErrorBanner(null)}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={5000}
        message={errorBanner || ''}
        hideBackdrop
      />
      <NavBar>
        <Box width="100%" height={({ theme }) => theme.sizes.appBarHeight} />
        <NavRow>
          <NavGroup start flex={1}>
            <NavBackButton onPress={() => handleNavigate()} label={'Back to Policy'} />
          </NavGroup>
        </NavRow>
      </NavBar>
      <Page backgroundColor="#F9FAFB" ref={scrollRef}>
        {status === 'pending' ? <Spinner /> : null}
        {status === 'error' ? <Text>Could not get policy information. Try refreshing the page.</Text> : null}
        {policy && monthlyReportingData && MCEDetails && (
          <LayoutBox width="100%" gap={40}>
            <MiscOverViewSection policy={policy} />
            <PolicyTabs
              data={policy}
              MCEDetails={MCEDetails}
              productCoverages={coveragesData}
              policyTabsList={tabs}
              quotesView={false}
              shouldShowWarning={shouldShowWarning}
              handleAddLocation={handleAddLocation}
              setShouldShowWarning={setShouldShowWarning}
              setShowSplitModal={setShowSplitModal}
              showSplitModal={showSplitModal}
              monthlyReportingData={monthlyReportingData}
              isEditingMCE={isEditing}
              setMCEDetails={setMCEDetails}
              updateMCE={updateMCE}
              setDeletedLocations={setDeletedLocations}
              setPremiumData={setPremiumData}
              premiumData={premiumData}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              mceId={MCEId}
              mceType={MCEDetails?.type}
              onMceMacavityComplete={() => refreshMCE()}
            />
            {MCEStatus === 'executed' && currentTab === 'MCE_FEES' && <AdjustmentSection policy={policy} />}
            <AttachmentSection policy={policy} />
            {shouldShowDraftModal && (
              <SaveDraftMCEModal
                showModal={shouldShowDraftModal}
                setShowModal={setShouldShowDraftModal}
                handleDiscardChanges={() => {
                  setShouldShowDraftModal(false);
                  navigate.back(-1);
                }}
                onClose={() => {
                  setShouldShowDraftModal(false);
                }}
                handlePress={() => {
                  handleClick();
                  setShouldShowDraftModal(false);
                  navigate.back(-1);
                }}
              />
            )}
            {loading && <LoaderModal loading={loading} setLoading={setLoading} />}
            <ExecuteMCEModal
              showModal={shouldExecute}
              setShowModal={setShouldExecute}
              handlePress={handleExecuteMCE}
              loading={updateStatus === 'pending'}
            />
          </LayoutBox>
        )}
      </Page>
    </LayoutBox>
  );
};

const MCE = () => {
  const { MCEDetails } = useMCEPageContext();

  const isMCElegacy = MCEDetails?.type === 'legacy';
  const { UpdateMCE, executeMCE, updateLegacyMCE, executeLegacyMCE } = useApi();

  return isMCElegacy ? (
    <NewMiscPage updateMCE={updateLegacyMCE} executeMCE={executeLegacyMCE} />
  ) : (
    <NewMiscPage updateMCE={UpdateMCE} executeMCE={executeMCE} />
  );
};

export const MCEPage = () => {
  return (
    <PolicyPageContextProvider>
      <MCEPageContextProvider>
        <MCE />
      </MCEPageContextProvider>
    </PolicyPageContextProvider>
  );
};
